import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, Navigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "./features/AuthContext";
import { useToast } from  "../hooks/useToast";
import { apiRequest } from "./lib/queryClient";

// 🔐 Zod schema
const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormInputs = z.infer<typeof loginSchema>;

// 🔁 API call function
const loginAPI = async (data: { username: string; password: string }) => {
  return await apiRequest("POST", "api/login", data);
};

export default function LoginScreen() {
  const { login: login_context, authorized } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
  });

  const loginMutation = useMutation({
    mutationFn: (data: LoginFormInputs) =>
      loginAPI({ username: data.email, password: data.password }),
    onSuccess: (response: any) => {
      const { user, token } = response.data;
      login_context(token, user);
      toast({
        title: "Success",
        description: "Logged in successfully",
      });
      navigate("/");
    },
    onError: (response:any) => {
      toast({
        title: "Login Failed",
        description:response?.message || "Invalid email or password",
        variant: "destructive",
      });
    },
  });

  if (authorized) return <Navigate to="/dashboard" />;

  const onSubmit = (data: LoginFormInputs) => {
    loginMutation.mutate(data);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white shadow-xl rounded-xl p-8">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <h2 className="text-2xl font-semibold text-center mb-6">Login</h2>

          {/* Email */}
          <label className="block text-sm text-gray-600 mb-1">Email</label>
          <input
            type="email"
            autoComplete="email"
            placeholder="Enter your email"
            {...register("email")}
            className="w-full border rounded px-3 py-2 mb-1 focus:outline-none focus:ring focus:border-gray-400"
          />
          {errors.email && (
            <p className="text-xs text-red-500 mb-3">{errors.email.message}</p>
          )}

          {/* Password */}
          <label className="block text-sm text-gray-600 mb-1">Password</label>
          <div className="relative mb-1">
            <input
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              {...register("password")}
              className="w-full border rounded px-3 py-2 pr-10 focus:outline-none focus:ring focus:border-gray-400"
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500"
              tabIndex={-1}
            >
              {showPassword ? "🙈" : "👁️"}
            </button>
          </div>
          {errors.password && (
            <p className="text-xs text-red-500 mb-4">
              {errors.password.message}
            </p>
          )}

          <button
            type="submit"
            disabled={loginMutation.isPending}
            className="w-full bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {loginMutation.isPending ? "Logging in..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}